import { useCallback, useEffect, useRef, useState } from "react";
import { Copy, Check, Mail, X, Loader2, AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import axios, { API } from "@/lib/apiClient";
import { toast } from "sonner";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const OutreachModal = ({ brandName, onClose, onSent }) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactError, setContactError] = useState(null);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const savedContactRef = useRef("");
  const contactEmailRef = useRef("");

  useEffect(() => {
    contactEmailRef.current = contactEmail;
  }, [contactEmail]);

  const generate = useCallback(() => {
    setLoading(true);
    setError(null);
    axios
      .post(`${API}/admin/outreach/generate`, { brand_name: brandName })
      .then((res) => {
        setSubject(res.data.subject);
        setBody(res.data.body);
        setContactEmail(res.data.contact_email || "");
        savedContactRef.current = res.data.contact_email || "";
        setSent(!!res.data.sent);
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || "Could not generate outreach draft.");
        toast.error("Could not generate outreach draft.");
      })
      .finally(() => setLoading(false));
  }, [brandName]);

  useEffect(() => {
    generate();
  }, [generate]);

  const saveContactIfChanged = useCallback(async () => {
    const trimmed = contactEmailRef.current.trim();
    if (trimmed === savedContactRef.current) return;
    if (trimmed && !EMAIL_RE.test(trimmed)) {
      setContactError("Enter a valid email address");
      return;
    }
    setContactError(null);
    try {
      await axios.put(`${API}/admin/outreach/contact`, { brand_name: brandName, contact_email: trimmed || null });
      savedContactRef.current = trimmed;
    } catch {
      toast.error("Could not save contact email.");
    }
  }, [brandName]);

  const requestClose = useCallback(() => {
    saveContactIfChanged();
    onClose();
  }, [saveContactIfChanged, onClose]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [requestClose]);

  const markSent = async (channel) => {
    try {
      await axios.post(`${API}/admin/outreach/mark-sent`, { brand_name: brandName, channel });
      setSent(true);
      onSent?.(brandName);
    } catch {
      toast.error("Could not record outreach as sent.");
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`);
      setCopied(true);
      toast.success("Outreach draft copied.");
      setTimeout(() => setCopied(false), 2000);
      markSent("copy");
    } catch {
      toast.error("Could not copy to clipboard. Select and copy the text manually.");
    }
  };

  const mailtoLink = !loading && !error
    ? `mailto:${contactEmail.trim()}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    : "#";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      data-testid="outreach-modal"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      <div className="w-full max-w-2xl rounded-lg border border-surface-border bg-surface-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="font-heading text-lg font-bold text-white">Sovereign Outreach - {brandName}</h2>
            {sent && (
              <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold text-emerald-400" data-testid="outreach-modal-sent-badge">
                Sent
              </span>
            )}
          </div>
          <button onClick={requestClose} className="text-slate-500 hover:text-white" data-testid="outreach-modal-close-btn">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading && (
          <div className="flex flex-col items-center justify-center gap-2 py-10 text-slate-400" data-testid="outreach-modal-loading">
            <Loader2 className="h-5 w-5 animate-spin" />
            <p>Drafting outreach email...</p>
            <p className="text-xs text-slate-600">This can take up to 30 seconds.</p>
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center gap-3 py-10 text-center" data-testid="outreach-modal-error">
            <AlertTriangle className="h-6 w-6 text-sovereign" />
            <p className="text-sm text-slate-300">{error}</p>
            <Button
              onClick={generate}
              variant="outline"
              className="gap-2 border-surface-border text-slate-200 hover:text-white"
              data-testid="outreach-modal-retry-btn"
            >
              <RotateCcw className="h-4 w-4" /> Retry
            </Button>
          </div>
        )}

        {!loading && !error && (
          <div className="space-y-3" data-testid="outreach-modal-content">
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">To</p>
              <Input
                type="email"
                placeholder="cmo@brand.com"
                value={contactEmail}
                onChange={(e) => {
                  setContactEmail(e.target.value);
                  if (contactError) setContactError(null);
                }}
                onBlur={saveContactIfChanged}
                className="border-surface-border bg-obsidian text-white"
                data-testid="outreach-to-input"
              />
              {contactError && (
                <p className="mt-1 text-xs text-sovereign" data-testid="outreach-to-error">{contactError}</p>
              )}
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Subject</p>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="border-surface-border bg-obsidian text-white"
                data-testid="outreach-subject-input"
              />
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Body</p>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={12}
                className="w-full resize-y rounded-md border border-surface-border bg-obsidian p-3 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-sovereign"
                data-testid="outreach-body-textarea"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={copy} variant="outline" className="gap-2 border-surface-border text-slate-200 hover:text-white" data-testid="outreach-copy-btn">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy to Clipboard"}
              </Button>
              <a href={mailtoLink} onClick={() => markSent("mailto")} data-testid="outreach-mailto-link">
                <Button className="gap-2 bg-sovereign text-white hover:bg-sovereign/90">
                  <Mail className="h-4 w-4" /> Open in Mail Client
                </Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
